const DIRECTORY_PREFIX = 'Directory - ';

const uniqPreserveOrder = (items = []) => {
  const out = [];
  const seen = new Set();
  for (const item of items) {
    const key = String(item || '');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(key);
  }
  return out;
};

export const normalizeUploadRelativePath = (rawPath) => {
  const normalized = String(rawPath || '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/^(\.\/|\/)+/, '');
  return normalized;
};

export const normalizeTopLevelName = (rawPath) => {
  const normalized = normalizeUploadRelativePath(rawPath);
  if (!normalized) return '';
  return normalized.split('/').filter(Boolean)[0] || '';
};

const isBlockedBySoftDeletedConflict = (uploadRelativePath, softDeletedConflicts = []) => {
  const normalized = normalizeUploadRelativePath(uploadRelativePath);
  if (!normalized) return false;

  for (const conflict of softDeletedConflicts) {
    const normalizedConflict = normalizeUploadRelativePath(conflict);
    if (!normalizedConflict) continue;
    if (normalized === normalizedConflict) return true;
    if (normalized.startsWith(`${normalizedConflict}/`)) return true;
  }

  return false;
};

export const normalizeConflictDisplayName = (entry) => {
  const text = String(entry || '').trim();
  if (text.startsWith(DIRECTORY_PREFIX)) {
    return text.slice(DIRECTORY_PREFIX.length).trim();
  }
  return text;
};

export const buildUploadConflictState = ({
  apiExistingConflicts = [],
  apiSoftDeletedConflicts = [],
  localConflicts = []
}) => {
  const softDeletedConflicts = uniqPreserveOrder(apiSoftDeletedConflicts);
  const softDeletedSet = new Set(softDeletedConflicts);

  const apiFsxOnly = uniqPreserveOrder(apiExistingConflicts).filter(
    (name) => !softDeletedSet.has(name)
  );

  const localFsxOnly = uniqPreserveOrder(localConflicts).filter((entry) => {
    const normalized = normalizeConflictDisplayName(entry);
    return normalized && !softDeletedSet.has(normalized);
  });

  const replaceConflicts = [];
  const seenNormalized = new Set();
  for (const entry of [...localFsxOnly, ...apiFsxOnly]) {
    const normalized = normalizeConflictDisplayName(entry);
    if (!normalized || seenNormalized.has(normalized)) continue;
    seenNormalized.add(normalized);
    replaceConflicts.push(entry);
  }

  return {
    replaceConflicts,
    softDeletedConflicts
  };
};

export const getUploadableFiles = ({
  acceptedFiles = [],
  replaceConflicts = [],
  softDeletedConflicts = [],
  mode = 'upload'
}) => {
  const replaceSet = new Set(
    replaceConflicts.map((entry) => normalizeConflictDisplayName(entry)).filter(Boolean)
  );
  return acceptedFiles.filter((file) => {
    const uploadRelativePath = normalizeUploadRelativePath(file && file.path);
    if (!uploadRelativePath) return false;
    if (isBlockedBySoftDeletedConflict(uploadRelativePath, softDeletedConflicts)) {
      return false;
    }

    const topLevelName = normalizeTopLevelName(uploadRelativePath);
    if (mode === 'replace') return true;
    return !replaceSet.has(topLevelName);
  });
};
